import { useState, useMemo } from 'react';
import { Copy, Check } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { RequireBusiness } from '@/components/RequireBusiness';
import { useApp } from '@/contexts/AppContext';

export default function HistoryPage() {
  return (
    <RequireBusiness>
      <HistoryContent />
    </RequireBusiness>
  );
}

function HistoryContent() {
  const { reviews, categories, markAsCopied } = useApp();
  const [filterCategory, setFilterCategory] = useState('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Filter and sort reviews: unused first, then used (faded) at bottom
  const sortedAndFilteredReviews = useMemo(() => {
    const filtered = filterCategory === 'all' 
      ? reviews 
      : reviews.filter(r => r.category_name === filterCategory);
    
    // Sort: unused reviews first, used reviews at bottom
    return [...filtered].sort((a, b) => {
      if (a.copied === b.copied) {
        // If same copied status, sort by date (newest first)
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
      // Unused (copied: false) comes first
      return a.copied ? 1 : -1;
    });
  }, [reviews, filterCategory]);

  const handleCopy = async (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    await markAsCopied(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  // Get unique category names for filter
  const uniqueCategories = [...new Set(reviews.map(r => r.category_name))];

  // Count submitted vs available
  const submittedCount = reviews.filter(r => r.submitted).length;
  const availableCount = reviews.length - submittedCount;

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col gap-3 mb-6">
          <div className="flex items-center justify-between">
            <h1 className="section-title">Review History</h1>
            <div className="text-xs text-muted-foreground">
              <span className="text-foreground font-medium">{availableCount}</span> available • <span className="text-primary font-medium">{submittedCount}</span> submitted
            </div>
          </div>
          
          {/* Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Filter:</span>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="flex-1 sm:flex-none sm:w-auto h-10 rounded-lg border border-input bg-background px-3 text-sm"
            >
              <option value="all">All Categories</option>
              {uniqueCategories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Reviews - Card Layout for Mobile */}
        <div className="space-y-3">
          {reviews.length === 0 ? (
            <div className="soft-card py-12 text-center">
              <p className="text-muted-foreground">No reviews generated yet</p>
            </div>
          ) : (
            sortedAndFilteredReviews.map((review) => (
              <div 
                key={review.id} 
                className={`soft-card p-4 transition-all duration-300 ${
                  review.copied 
                    ? 'opacity-50 bg-muted/30' 
                    : ''
                }`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="inline-block text-xs font-medium text-primary-foreground bg-primary px-2 py-0.5 rounded">
                        {review.category_name}
                      </span>
                      {review.submitted && (
                        <span className="inline-block text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
                          Submitted
                        </span>
                      )}
                    </div>
                    <p className="text-sm leading-relaxed line-clamp-3">
                      {review.review_text}
                    </p>
                  </div>
                  <button
                    onClick={() => handleCopy(review.id, review.review_text)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors flex-shrink-0"
                    disabled={review.copied}
                  >
                    {copiedId === review.id ? (
                      <Check size={18} className="text-primary" />
                    ) : (
                      <Copy size={18} className={review.copied ? "text-muted" : "text-muted-foreground"} />
                    )}
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground pt-2 border-t border-border">
                  <span>{formatDate(review.created_at)}</span>
                  {review.keywords && review.keywords.length > 0 && (
                    <>
                      <span>•</span>
                      <span className="truncate max-w-[150px]">
                        {review.keywords.slice(0, 2).join(', ')}
                        {review.keywords.length > 2 && '...'}
                      </span>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </AppLayout>
  );
}
